import { Spinner } from "@/components/data-state";
import { GoalBadge } from "@/components/goal-badge";
import { Badge } from "@/components/ui/badge";
import type { SimSession } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * One badge that says what became of a session.
 *
 * The scored case is the leaderboard's tier chip; the rest are the ways a run can end
 * without a score, which a student needs to tell apart -- their own `abort()` is not the
 * idle timeout is not a crash on our side -- plus the two states before it has ended.
 */
export function AttemptOutcome({
  session,
  className,
}: {
  session: Pick<SimSession, "status" | "result" | "end_reason">;
  className?: string;
}) {
  switch (session.status) {
    case "pending":
      return (
        <Badge variant="secondary" className={cn("gap-1.5", className)}>
          <Spinner className="size-3" />
          Reserved
        </Badge>
      );
    case "running":
      return (
        <Badge
          variant="outline"
          className={cn(
            "gap-1.5 border-primary/40 bg-primary/10 text-primary",
            className,
          )}
        >
          <span className="relative flex size-2" aria-hidden>
            <span className="bg-primary absolute inline-flex size-full animate-ping rounded-full opacity-60" />
            <span className="bg-primary relative inline-flex size-2 rounded-full" />
          </span>
          Live
        </Badge>
      );
    case "scored":
      return <GoalBadge tier={session.result?.goal_tier ?? null} className={className} />;
    case "aborted":
      return (
        <Badge variant="secondary" className={className} title={session.end_reason ?? undefined}>
          Aborted
        </Badge>
      );
    case "expired":
      return (
        <Badge variant="secondary" className={className} title={session.end_reason ?? undefined}>
          Expired
        </Badge>
      );
    case "failed":
      return (
        <Badge variant="destructive" className={className} title={session.end_reason ?? undefined}>
          Failed
        </Badge>
      );
  }
}
